import React, { useMemo, useState } from "react"

import { css } from "@emotion/react"
import { useFormContext } from "react-hook-form"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import { IOneVerifyHistoryRetrySingleRequestData } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import { IOneVerifyCoverageResult } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { TooltipElement } from "@front-shared/elements/tooltip.element"

import { CoverageCheckerMode } from "../../../../config/checker.config"
import { COVERAGE_CONFIG } from "../../../../config/coverage.config"
import { POLICIES_CONFIG } from "../../../../config/policies.config"
import { TOOLTIPS_CONFIG } from "../../../../config/tooltips.config"
import { AmplitudeHelper } from "../../../../helpers/amplitude.helper"
import { CheckerHelper } from "../../../../helpers/checker.helper"
import { PoliciesHelper } from "../../../../helpers/policies.helper"
import { RuntimeHelper } from "../../../../helpers/runtime.helper"
import { useHasPermissions } from "../../../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../../../hooks/isFeatureFlagEnabled.hook"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { policiesSelector } from "../../../../store/selectors/policies.selector"
import { runtimeSelector } from "../../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSelector } from "../../../../store/selectors/userDetails.selector"
import { policiesRetrySinglePolicy } from "../../../../store/thunks/policies.thunks"

import refreshIcon from "@front-shared/assets/images/icons/refresh-dark2.svg"

export const SinglePolicyRetryButtonComponent: React.FunctionComponent<
    ISinglePolicyRetryButtonComponentProps
> = props => {
    const { selectedPracticeUsageQuota } = useAppSelector(userDetailsSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useFormContext<IOneVerifyHistoryRetrySingleRequestData>()

    const [isPopupActive, setIsPopupActive] = useState<boolean>(false)
    const [isUpdatingPatientId, setIsUpdatingPatientId] =
        useState<boolean>(false)

    const policiesState = useAppSelector(policiesSelector)
    const { selectedItem } = policiesState

    const dispatch = useAppDispatch()

    const patientId = watch("patientId")

    const existingPatientId = useMemo(() => {
        return (
            selectedItem?.coverageResult.customerPatientId ||
            selectedItem?.requestData.customerPatientId
        )
    }, [selectedItem])

    const isRetryingCoverageCheckLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isRetrySinglePolicyLoading()
    }, [runtimeState.isLoading])

    const ehrWriteFlagEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EHR_WRITE_SINGLE,
    )

    const hasRunChecksPermission = useHasPermissions(
        OneVerifyPermission.runCoverageChecks,
        undefined,
        false,
    )

    const isPolicyRecheckEligible = useMemo<boolean>(() => {
        return selectedItem
            ? PoliciesHelper.isPolicyRecheckEligible(selectedItem)
            : false
    }, [selectedItem])

    const retryCoverageCheckQuotaConfirmation = () => {
        // Should not return early if functioning normally
        if (
            !selectedPracticeUsageQuota ||
            !isPolicyRecheckEligible ||
            !selectedItem?.coverageResult ||
            !selectedPracticeRole?.practice
        ) {
            return
        }

        // Explicitly pass undefined to avoid passing empty string; do this before resetting the form and closing the modal
        const formattedPatientId = patientId?.trim() || undefined

        // Close the modal and reset the form after retrieving the patient ID
        onDone()

        CheckerHelper.runChecksWithQuotaConfirmation(
            selectedPracticeUsageQuota,
            () => {
                const amplitudeEvent = formattedPatientId
                    ? existingPatientId
                        ? AmplitudeEvent.RE_CHECK_PATIENT_ID_UPDATED
                        : AmplitudeEvent.RE_CHECK_PATIENT_ID_ADDED
                    : AmplitudeEvent.RE_CHECK_PATIENT_ID_NO_CHANGE

                AmplitudeHelper.logEvent(amplitudeEvent, {
                    practiceId: selectedPracticeRole.practice.id,
                    practiceName: selectedPracticeRole.practice.name,
                })

                dispatch(
                    policiesRetrySinglePolicy({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            nirvanaRequestId:
                                selectedItem.coverageResult.resultId,
                            patientId: formattedPatientId,
                        },
                        onSuccess: (result: IOneVerifyCoverageResult) => {
                            result.requestId &&
                                props.navigateToPolicyByRequestId(
                                    result.requestId,
                                )
                        },
                    }),
                )
            },
            selectedItem?.coverageResult.planType &&
                COVERAGE_CONFIG.medicaidPlanTypes.includes(
                    selectedItem.coverageResult.planType,
                )
                ? QuotaUsageType.MEDICAID
                : QuotaUsageType.GENERAL,
            1,
        )
    }

    /*
     * When button is pressed we check if the user has the EHR write flag enabled
     * If it is enabled we show the popup to add or change the patient ID associated with the check before calling retryCoverageCheckQuotaConfirmation
     * If it is not enabled we call retryCoverageCheckQuotaConfirmation directly
     * retryCoverageCheckQuotaConfirmation will check if the user has enough quota to run the check
     * If the user has enough quota it will run the check
     * If the user does not have enough quota it will show one of the quota popups accordingly
     */
    const handleRetryCoverageCheck = () => {
        if (
            ehrWriteFlagEnabled &&
            !POLICIES_CONFIG.ehrWriteDisabledListTypes.includes(props.listType)
        ) {
            setIsPopupActive(true)
        } else {
            retryCoverageCheckQuotaConfirmation()
        }
    }

    const onDone = (logCancelEvent = false) => {
        if (logCancelEvent && selectedPracticeRole?.practice) {
            AmplitudeHelper.logEvent(AmplitudeEvent.RE_CHECK_MODAL_CANCELED, {
                practiceId: selectedPracticeRole.practice.id,
                practiceName: selectedPracticeRole.practice.name,
            })
        }

        setIsPopupActive(false)

        setIsUpdatingPatientId(false)

        setValue("patientId", undefined)
    }

    if (
        !hasRunChecksPermission ||
        props.listType === PoliciesListType.AGENTIC
    ) {
        return null
    }

    return (
        <div className={props.className}>
            <ButtonElement
                buttonClassName="sm:w-full"
                htmlType="button"
                type="default"
                label="Run Check"
                icon={refreshIcon}
                isRightIcon={true}
                disabled={!isPolicyRecheckEligible}
                isLoading={isRetryingCoverageCheckLoading}
                onClick={handleRetryCoverageCheck}
            />

            {!isPolicyRecheckEligible && (
                <TooltipElement
                    inlineLabel={
                        TOOLTIPS_CONFIG.singlePolicyView.cantReRunCheckText
                            .title
                    }
                    inlineLabelTypography="captionSemibold"
                    isInlined
                    text={TOOLTIPS_CONFIG.singlePolicyView.cantReRunCheckText.text(
                        CoverageCheckerMode.MANUAL,
                    )}
                    backgroundColor={NIRVANA_COLORS.brand.black}
                />
            )}

            <PopupWrapperElement
                isActive={isPopupActive}
                onDone={onDone}
                closeOnOutsideClick={true}
                popupWidthClassName="w-[720px]"
            >
                <form
                    onSubmit={handleSubmit(retryCoverageCheckQuotaConfirmation)}
                    noValidate={true}
                >
                    {!existingPatientId ? (
                        <>
                            <PrimaryText typography="h4">
                                Add Patient ID / Medical Record Number to Check
                            </PrimaryText>

                            <PrimaryText
                                typography="text"
                                css={css({
                                    marginTop: 32,
                                })}
                            >
                                No patient ID is currently specified for this
                                check. A patient ID is required to write results
                                to EHR.
                            </PrimaryText>
                        </>
                    ) : (
                        <PrimaryText typography="h4">
                            Update Patient ID / Medical Record Number for Check
                        </PrimaryText>
                    )}

                    {existingPatientId && !isUpdatingPatientId && (
                        <PrimaryText
                            typography="text"
                            css={css({
                                marginTop: 32,
                            })}
                        >
                            The current patient ID is:{" "}
                            <PrimaryText
                                typography="textSemibold"
                                className="inline"
                            >
                                {existingPatientId}
                            </PrimaryText>
                            . Would you like to run the check again with this
                            patient ID or update the patient ID?
                        </PrimaryText>
                    )}

                    {(isUpdatingPatientId || !existingPatientId) && (
                        <InputElement
                            name="patientId"
                            label={`Patient ID${isUpdatingPatientId ? "" : " (optional)"}`}
                            className="mt-[32px]"
                            reactHookControl={control as any}
                            reactHookValidations={{
                                maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                                required: isUpdatingPatientId
                                    ? FRONT_VALIDATION_CONFIG.required
                                    : false,
                            }}
                            shouldUnregister={false}
                            reactHookErrors={errors}
                        />
                    )}

                    <div className="flex items-center justify-between mt-[32px]">
                        <ButtonElement
                            buttonClassName="mr-[16px]"
                            label="Cancel"
                            type="default"
                            htmlType="button"
                            size="large"
                            onClick={() => {
                                onDone(true)
                            }}
                        />

                        <div className="flex items-center justify-between gap-[16px]">
                            {!!existingPatientId && (
                                <ButtonElement
                                    label={
                                        isUpdatingPatientId
                                            ? "Never mind"
                                            : "Update Patient ID"
                                    }
                                    type="default"
                                    size="large"
                                    htmlType="button"
                                    onClick={() => {
                                        if (isUpdatingPatientId) {
                                            setIsUpdatingPatientId(false)

                                            setValue("patientId", undefined)
                                        } else {
                                            setIsUpdatingPatientId(true)
                                        }
                                    }}
                                />
                            )}

                            <ButtonElement
                                label="Run Check"
                                type="primary"
                                size="large"
                                disabled={isUpdatingPatientId && !patientId}
                                htmlType="submit"
                            />
                        </div>
                    </div>
                </form>
            </PopupWrapperElement>
        </div>
    )
}

interface ISinglePolicyRetryButtonComponentProps {
    className?: string
    listType: PoliciesListType
    navigateToPolicyByRequestId: (requestId: string) => void
}
